import styled from 'styled-components';

/** Tokens light forçados em telas públicas (login, recover, etc.). */
const lightTokens = `
  --surface-1: #ffffff;
  --surface-2: #f4f9f7;
  --surface-3: #e9f3f0;
  --text-primary: #16343c;
  --text-secondary: #3d5960;
  --text-muted: #6d858a;
  --text-heading: #075d3a;
  --text-on-brand: #ffffff;
  --text-on-accent: #ffffff;
  --border-subtle: rgba(0, 75, 62, 0.12);
  --border-default: rgba(0, 96, 72, 0.26);
  --border-strong: rgba(0, 112, 75, 0.48);
  --accent: #008c4a;
  --accent-hover: #00783f;
  --accent-soft: rgba(0, 140, 74, 0.12);
  --accent-secondary: #16734b;
  --success: #087f4d;
  --warning: #9a6500;
  --danger: #c93636;
  --info: #087fa3;
  --input-bg: #f4f9f7;
  --surface-hover: rgba(0, 140, 74, 0.08);
  --shadow-md: 0 8px 28px rgba(14, 55, 48, 0.14);
  --shadow-lg: 0 18px 55px rgba(14, 55, 48, 0.18);
  --brand-cta-gradient-135: linear-gradient(135deg, #008c4a 0%, #0a6b8a 100%);
`;

export const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: ${({ $forceLight }) =>
    $forceLight
      ? 'rgba(14, 55, 48, 0.38)'
      : 'color-mix(in srgb, var(--brand-navy, #00212d) 52%, transparent)'};
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 1rem;
`;

export const ModalContainer = styled.div`
  ${({ $forceLight }) => ($forceLight ? lightTokens : '')}
  background: var(--surface-1);
  border: 1px solid var(--border-subtle);
  border-radius: 18px;
  box-shadow: var(--shadow-lg);
  width: min(100%, 420px);
  min-height: 220px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${({ type }) => {
      switch (type) {
        case 'success':
          return 'linear-gradient(135deg, var(--success) 0%, var(--accent) 100%)';
        case 'error':
          return 'linear-gradient(135deg, var(--danger) 0%, #a02828 100%)';
        case 'warning':
          return 'linear-gradient(135deg, var(--warning) 0%, #c48a12 100%)';
        case 'loading':
          return 'var(--brand-cta-gradient-135)';
        default:
          return 'var(--brand-cta-gradient-135)';
      }
    }};
    z-index: 2;
  }
`;

export const ModalContent = styled.div`
  padding: 0;
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

export const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 1.5rem 1.5rem 1rem;
  border-bottom: 1px solid var(--border-subtle);
  background: color-mix(in srgb, var(--surface-2) 70%, transparent);
  position: relative;
`;

export const ModalIcon = styled.div`
  font-size: 1.35rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.75rem;
  height: 2.75rem;
  flex-shrink: 0;
  background: var(--accent-soft);
  border-radius: 50%;
  border: 2px solid var(--accent);
  color: var(--text-heading);
`;

export const ModalTitle = styled.h2`
  color: var(--text-heading);
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0;
  flex: 1;
  line-height: 1.3;
  padding-right: 1.5rem;
`;

export const CloseButton = styled.button`
  position: absolute;
  top: 0.85rem;
  right: 0.85rem;
  background: var(--surface-3);
  border: none;
  color: var(--text-secondary);
  font-size: 1.25rem;
  cursor: pointer;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
  line-height: 1;

  &:hover {
    background: var(--surface-hover);
    color: var(--text-heading);
  }
`;

export const ModalBody = styled.div`
  padding: 1.25rem 1.5rem;
`;

export const ModalMessage = styled.p`
  color: var(--text-secondary);
  font-size: 0.95rem;
  line-height: 1.55;
  margin: 0;
  text-align: left;
  white-space: pre-line;
`;

export const ModalFooter = styled.div`
  display: flex;
  gap: 0.65rem;
  padding: 0.5rem 1.5rem 1.5rem;
  justify-content: flex-end;
  flex-wrap: wrap;
`;

export const ModalButton = styled.button`
  padding: 0.7rem 1.5rem;
  border-radius: 999px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s ease;
  border: 2px solid transparent;
  min-width: 110px;
  position: relative;
  overflow: hidden;

  ${props => {
    switch (props.type) {
      case 'success':
        return `
          background: var(--accent);
          color: var(--text-on-brand);
          border-color: var(--accent);

          &:hover {
            background: var(--accent-hover);
            transform: translateY(-2px);
            box-shadow: var(--shadow-md);
          }
        `;
      case 'error':
        return `
          background: var(--danger);
          color: var(--text-on-brand);
          border-color: var(--danger);

          &:hover {
            filter: brightness(0.95);
            transform: translateY(-2px);
            box-shadow: var(--shadow-md);
          }
        `;
      case 'warning':
        return `
          background: var(--warning);
          color: var(--text-on-brand);
          border-color: var(--warning);

          &:hover {
            filter: brightness(0.95);
            transform: translateY(-2px);
            box-shadow: var(--shadow-md);
          }
        `;
      case 'cancel':
        return `
          background: transparent;
          color: var(--text-muted);
          border-color: var(--border-default);

          &:hover {
            background: var(--surface-2);
            border-color: var(--border-strong);
            color: var(--text-primary);
            transform: translateY(-2px);
          }
        `;
      default:
        return `
          background: var(--accent);
          color: var(--text-on-brand);
          border-color: var(--accent);

          &:hover {
            background: var(--accent-hover);
            transform: translateY(-2px);
            box-shadow: var(--shadow-md);
          }
        `;
    }
  }}

  &:active {
    transform: translateY(0);
  }
`;
